import { medalTierFromScore, type MedalLedger } from './medals';
import type { LocalProfile } from './localProfile';

export type Achievement = {
  id: string;
  emoji: string;
  title: string;
  blurb: string;
  unlocked: boolean;
};

export function medalCount(medals: MedalLedger) {
  return medals.bronze + medals.silver + medals.gold + medals.platinum;
}

export function personalHeadline(
  profile: Pick<LocalProfile, 'games_played' | 'current_streak' | 'max_streak' | 'high_score'>,
  medals: MedalLedger
) {
  const games = profile.games_played;
  const streak = profile.current_streak;
  const best = profile.high_score;
  const plats = medals.platinum;
  const golds = medals.gold;

  if (games === 0) {
    return { headline: 'Your record starts today.', sub: 'Finish a puzzle and this page comes alive.' };
  }
  if (plats >= 1 && best === 490) {
    return { headline: 'You’ve touched the ceiling.', sub: 'A Platinum lives in your cabinet. That’s rare air.' };
  }
  if (streak >= 14) {
    return { headline: `${streak} days. Unreasonable.`, sub: 'The streak is the whole personality now.' };
  }
  if (streak >= 7) {
    return { headline: `${streak}-day streak. Protect it.`, sub: 'A week of showing up. Don’t you dare ghost tomorrow.' };
  }
  if (streak >= 3) {
    return { headline: `${streak} days in a row.`, sub: 'Momentum looks good on you.' };
  }
  if (golds >= 5) {
    return { headline: 'A gold habit.', sub: `${golds} golds. You’re not dabbling anymore.` };
  }
  if (best >= 300) {
    return { headline: `Best: ${best}. That’s Gold.`, sub: 'You’ve already proven you can cook.' };
  }
  if (games >= 50) {
    return { headline: `${games} puzzles deep.`, sub: 'That’s not a hobby. That’s a ritual.' };
  }
  if (games >= 10) {
    return { headline: 'You’re in the club.', sub: `${games} games. The list knows your name.` };
  }
  return {
    headline: 'The record is open.',
    sub: streak > 0 ? `Streak of ${streak}. Keep the lights on.` : 'Show up tomorrow and it starts to look like a story.',
  };
}

export function buildAchievements(profile: LocalProfile, medals: MedalLedger): Achievement[] {
  const games = profile.games_played;
  const streak = profile.current_streak;
  const max = profile.max_streak;
  const best = profile.high_score;
  const total = medalCount(medals);
  const types = [medals.bronze, medals.silver, medals.gold, medals.platinum].filter((n) => n > 0).length;

  return [
    { id: 'first', emoji: '🎬', title: 'First blood', blurb: 'Finish your first official game.', unlocked: games >= 1 },
    { id: 'ten', emoji: '🔟', title: 'Regular', blurb: 'Play 10 puzzles.', unlocked: games >= 10 },
    { id: 'fifty', emoji: '📅', title: 'Dedicated', blurb: 'Play 50 puzzles.', unlocked: games >= 50 },
    { id: 'hundred', emoji: '💯', title: 'Centurion', blurb: 'Play 100 puzzles.', unlocked: games >= 100 },
    { id: 'streak3', emoji: '🔥', title: 'On a roll', blurb: 'A 3-day streak.', unlocked: streak >= 3 || max >= 3 },
    { id: 'streak7', emoji: '🗓️', title: 'Week warrior', blurb: 'A 7-day streak.', unlocked: streak >= 7 || max >= 7 },
    { id: 'streak30', emoji: '🏔️', title: 'Unbreakable', blurb: 'A 30-day streak.', unlocked: max >= 30 },
    { id: 'bronze', emoji: '🥉', title: 'On the board', blurb: 'Earn a bronze.', unlocked: medals.bronze >= 1 || total >= 1 },
    { id: 'silver', emoji: '🥈', title: 'Silver lining', blurb: 'Earn a silver.', unlocked: medals.silver >= 1 },
    { id: 'gold', emoji: '🥇', title: 'Gold rush', blurb: 'Earn a gold.', unlocked: medals.gold >= 1 },
    { id: 'platinum', emoji: '💎', title: 'The ceiling', blurb: 'Earn a platinum.', unlocked: medals.platinum >= 1 },
    { id: 'perfect', emoji: '🎯', title: 'Perfect game', blurb: 'Hit 490.', unlocked: best === 490 },
    { id: 'deep', emoji: '🪙', title: 'Deep pockets', blurb: 'A best score of 300+.', unlocked: best >= 300 },
    { id: 'collector', emoji: '🏆', title: 'Full cabinet', blurb: 'Own every medal tier.', unlocked: types === 4 },
    { id: 'hoarder', emoji: '📦', title: 'Medal hoarder', blurb: 'Collect 25 medals.', unlocked: total >= 25 },
  ];
}

export function bestMedalIcon(score: number) {
  const tier = medalTierFromScore(score);
  if (tier === 'platinum') return '💎';
  if (tier === 'gold') return '🥇';
  if (tier === 'silver') return '🥈';
  if (tier === 'bronze') return '🥉';
  return null;
}
